import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardMedia,
  Button,
  Stack,
  IconButton,
  Dialog,
} from '@mui/material';
import { TechnicianDoc } from '../../../utils/entities/interface';
import {
  TechnicianKycStatus,
  TechnicianStatus,
} from '../../../utils/entities/type';
import {
  AddDocument,
  ToggleTechnicianStatus,
  UpdateKycStatus,
} from '../../../services/technician';
import { DialogBoxComp } from '../../../components/atoms/DialogBox';
import useSnackbar from '../../../hooks/useSnackbar';
import { Close, RemoveRedEyeOutlined } from '@mui/icons-material';
import { DocumentUploadModal } from '../../../components/ui/add/documentModal';
import { RotatableImage } from '../../../components/atoms/RotatableImage';

interface PropsManageKYC {
  document: TechnicianDoc[];
  kycStatus: TechnicianKycStatus;
  status: TechnicianStatus;
  technicianId: string;
  onChanges: () => void;
  onCloseDrawer: () => void;
  disable: boolean;
  CompleteProfile: React.Dispatch<React.SetStateAction<boolean>>;
  refreshTechnician: () => void;
}

export const ManageKYC = ({
  document,
  kycStatus,
  status,
  technicianId,
  onChanges,
  disable,
  onCloseDrawer,
  CompleteProfile,
  refreshTechnician,
}: PropsManageKYC) => {
  const [isUploadDocumentModal, setUploadDocumentModal] =
    useState<boolean>(false);

  const shouldCreateProfile = status == 'SIGNED_UP';

  const shouldUploadDocument =
    (['PROFILE_CREATED', 'KYC_PENDING'].includes(status) ||
      kycStatus == 'TBU') &&
    document.length < 2 &&
    !shouldCreateProfile;

  const shouldShownDocuments = Array.isArray(document) && document.length > 0;

  const AllowedActions = Array.isArray(document) && document.length >= 2;

  const CanReUploadDocuments = Array.isArray(document) && document.length >= 2;

  return (
    <Box sx={{ flex: 1, padding: 0, display: 'flex', flexDirection: 'column' }}>
      {shouldCreateProfile && (
        <Box
          textAlign="center"
          p={3}
          borderRadius={2}
          bgcolor="rgba(60, 20, 20, 0.9)"
        >
          <Typography variant="h6" color="error.main" gutterBottom>
            Complete Your Technician Profile
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            To continue, you need to complete your technician profile. Would you
            like to do it now or later?
          </Typography>
          <Stack direction="row" spacing={2} justifyContent="center">
            <Button
              variant="contained"
              sx={{
                bgcolor: 'error.dark',
                '&:hover': { bgcolor: 'error.main' },
              }}
              onClick={() => CompleteProfile(true)}
            >
              Complete Now
            </Button>
            <Button
              variant="outlined"
              sx={{
                color: 'warning.light',
                borderColor: 'warning.light',
                '&:hover': {
                  borderColor: 'warning.main',
                  color: 'warning.main',
                },
              }}
              onClick={onCloseDrawer}
            >
              Do It Later
            </Button>
          </Stack>
        </Box>
      )}

      {/* KYC Completion Prompt */}
      {shouldUploadDocument && (
        <Box
          textAlign="center"
          p={3}
          borderRadius={2}
          bgcolor="rgba(60, 20, 20, 0.9)"
          mb={2}
        >
          <Typography variant="h6" color="success.main" gutterBottom>
            Complete Your KYC Verification
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            KYC verification is pending. Please Upload Documents to continue.
          </Typography>
          <Stack direction="row" spacing={2} justifyContent="center">
            <Button
              variant="contained"
              sx={{
                bgcolor: 'success.dark',
                '&:hover': { bgcolor: 'success.main' },
              }}
              onClick={() => setUploadDocumentModal(true)}
            >
              Upload Documents
            </Button>
            <Button
              variant="outlined"
              sx={{
                color: 'info.light',
                borderColor: 'info.light',
                '&:hover': {
                  borderColor: 'info.main',
                  color: 'info.main',
                },
              }}
              onClick={onCloseDrawer}
            >
              Do It Later
            </Button>
          </Stack>
        </Box>
      )}

      {shouldShownDocuments && (
        <Stack spacing={2} sx={{ flex: 1 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
              gap: 2,
            }}
          >
            {document.map((doc, index) => (
              <Documents
                key={index}
                document={doc}
                technicianId={technicianId}
                onDocumentUpdated={refreshTechnician}
              />
            ))}
          </Box>
        </Stack>
      )}

      {shouldUploadDocument && (
        <Box
          textAlign="center"
          p={2}
          borderRadius={2}
          bgcolor="rgba(255, 160, 0, 0.15)"
          color="warning.light"
          border="1px solid rgba(255, 160, 0, 0.4)"
          mb={2}
        >
          <Typography variant="h6" color="warning.main" gutterBottom>
            Minimum 2 Documents Required
          </Typography>
          <Typography variant="body2" color="grey.300">
            You have uploaded only {document.length} documents. Upload at least
            2 documents to proceed.
          </Typography>
        </Box>
      )}

      {CanReUploadDocuments && (
        <Box
          textAlign="center"
          p={3}
          borderRadius={2}
          bgcolor="rgba(60, 20, 20, 0.9)"
          mb={2}
        >
          <Typography variant="h6" color="success.main" gutterBottom>
            Minimum Documents Submitted
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            You can re-upload both documents or replace the existing ones.
          </Typography>
          <Stack direction="row" spacing={2} justifyContent="center">
            <Button
              variant="contained"
              color="success"
              onClick={() => setUploadDocumentModal(true)}
            >
              Upload Documents
            </Button>
          </Stack>
        </Box>
      )}

      {AllowedActions && (
        <ActionButtons
          kycStatus={kycStatus}
          Status={status}
          technicianId={technicianId}
          onChanges={() => {
            refreshTechnician();
            onCloseDrawer();
          }}
        />
      )}

      <DocumentUploadModal
        open={isUploadDocumentModal}
        onClose={() => setUploadDocumentModal(false)}
        onSubmit={refreshTechnician}
        onReload={refreshTechnician}
        technicianId={technicianId}
      />
    </Box>
  );
};

const ActionButtons = ({
  kycStatus,
  Status,
  technicianId,
  onChanges,
}: {
  kycStatus: TechnicianKycStatus;
  Status: TechnicianStatus;
  technicianId: string;
  onChanges: () => void;
}) => {
  const [openModal, setOpenModal] = useState<boolean>(false);
  const { SnackbarComponent, showSnackbar } = useSnackbar();

  const handleKycChange = async (status: 'APPROVE' | 'REJECT' | 'REQUEST') => {
    if (!technicianId) return;

    UpdateKycStatus(technicianId, status)
      .then((responseStatus: number) => {
        if (responseStatus === 200) {
          showSnackbar('Successfully Updated kyc', 'success');
          setTimeout(() => {
            onChanges();
          }, 700);
        }
      })
      .catch((error: Error) => {
        showSnackbar('Failed To Update Kyc', 'error');
      });
  };

  const handleToggleActiveStatus = () => {
    if (!technicianId) return;

    ToggleTechnicianStatus(technicianId)
      .then((status: number) => {
        if (status === 200) {
          showSnackbar('Successfully Updated kyc', 'success');
          setTimeout(() => {
            onChanges();
          }, 700);
        }
      })
      .catch((error: Error) => {
        console.error('Error toggling technician status:', error.message);
      });
  };

  return (
    <>
      {SnackbarComponent}

      {Status == 'PROFILE_CREATED' && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: 2,
            marginTop: 3,
            alignSelf: 'flex-end',
          }}
        >
          <Button
            variant="contained"
            color="success"
            onClick={() => handleKycChange('APPROVE')}
          >
            Approve
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => handleKycChange('REJECT')}
          >
            Reject
          </Button>
        </Box>
      )}

      {kycStatus === 'REQUESTED' && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: 2,
            marginTop: 3,
            alignSelf: 'flex-end',
          }}
        >
          <Button
            variant="contained"
            color="success"
            onClick={() => handleKycChange('APPROVE')}
          >
            Approve
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => handleKycChange('REJECT')}
          >
            Reject
          </Button>
        </Box>
      )}

      {kycStatus === 'REJECTED' && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: 2,
            marginTop: 3,
            alignSelf: 'flex-end',
          }}
        >
          <Button
            variant="contained"
            color="success"
            onClick={() => handleKycChange('APPROVE')}
          >
            Approve
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => handleKycChange('REQUEST')}
          >
            Request
          </Button>
        </Box>
      )}

      {kycStatus === 'VERIFIED' && Status == 'DISABLED' && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: 2,
            marginTop: 3,
            alignSelf: 'flex-end',
          }}
        >
          <Button
            variant="contained"
            color="success"
            onClick={() => handleKycChange('REQUEST')}
          >
            Request
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleToggleActiveStatus}
          >
            Enable
          </Button>
        </Box>
      )}

      {kycStatus === 'VERIFIED' && Status != 'DISABLED' && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: 2,
            marginTop: 3,
            alignSelf: 'flex-end',
          }}
        >
          <Button
            variant="contained"
            color="error"
            onClick={handleToggleActiveStatus}
          >
            Disable
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={() => setOpenModal(true)}
          >
            Request
          </Button>
        </Box>
      )}

      {kycStatus === 'PENDING' && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: 2,
            marginTop: 3,
            alignSelf: 'flex-end',
          }}
        >
          <Button
            variant="contained"
            color="success"
            onClick={() => setOpenModal(true)}
          >
            Request
          </Button>
        </Box>
      )}

      <DialogBoxComp
        open={openModal}
        title="Request Kyc Documents"
        content="Are you sure want to request for a new kyc documents ?"
        handleClose={() => setOpenModal(false)}
        handleConfirm={() => {
          handleKycChange('REQUEST');
          setOpenModal(false);
        }}
      />
    </>
  );
};

const Documents = ({
  document,
  technicianId,
  onDocumentUpdated,
}: {
  document: TechnicianDoc;
  technicianId: string;
  onDocumentUpdated: () => void;
}) => {
  const [openPreview, setOpenPreview] = useState<boolean>(false);
  const [savingRotation, setSavingRotation] = useState<boolean>(false);
  const { SnackbarComponent, showSnackbar } = useSnackbar();

  const handleOpenPreview = () => {
    setOpenPreview(true);
  };

  const handleClosePreview = () => {
    setOpenPreview(false);
  };

  const handleSaveRotatedDocument = async (newUrl: string) => {
    if (!technicianId || !document.type) return;

    try {
      const status = await AddDocument({
        technicianId,
        type: document.type,
        docUrl: newUrl,
        comment: document.comment || '',
      });

      if (status === 200) {
        showSnackbar('Document rotation saved successfully.', 'success');
        onDocumentUpdated();
      }
    } catch (error) {
      console.error('Document rotation save error:', error);
      showSnackbar('Failed to save rotated document.', 'error');
    }
  };

  return (
    <>
      {SnackbarComponent}

      <Card
        sx={{
          backgroundColor: '#252525',
          borderRadius: 2,
          padding: 1.5,
          textAlign: 'center',
        }}
      >
        {/* Document Image */}
        <CardMedia
          component="img"
          src={document.url}
          alt={document.type}
          onClick={handleOpenPreview}
          sx={{
            width: '100%',
            height: 120,
            borderRadius: 2,
            objectFit: 'cover',
            cursor: 'pointer',
          }}
        />

        {/* Label & Open Icon */}
        <Box
          mt={1}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography
            variant="caption"
            sx={{
              color: '#fff',
              fontWeight: 600,
              backgroundColor: '#5E167A',
              padding: '2px 6px',
              borderRadius: 4,
              textTransform: 'uppercase',
              fontSize: 10,
            }}
          >
            {document.type?.replace('_', ' ') || 'Document'}
          </Typography>

          <IconButton size="small" onClick={handleOpenPreview}>
            <RemoveRedEyeOutlined sx={{ fontSize: 16, color: '#fff' }} />
          </IconButton>
        </Box>
      </Card>

      <Dialog
        open={openPreview}
        onClose={handleClosePreview}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#111',
            borderRadius: 3,
            overflow: 'hidden',
            position: 'relative',
          },
        }}
      >
        <IconButton
          onClick={handleClosePreview}
          sx={{
            position: 'absolute',
            top: 10,
            right: 10,
            zIndex: 10,
            color: '#fff',
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
            },
          }}
        >
          <Close />
        </IconButton>

        <Box
          sx={{
            width: '100%',
            maxHeight: '85vh',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: '#111',
            p: 2,
          }}
        >
          <RotatableImage
            src={document.url}
            alt={document.type}
            folderType={4}
            onRotated={handleSaveRotatedDocument}
            onSaving={setSavingRotation}
            containerSx={{ width: '100%', minHeight: '70vh' }}
            imageStyle={{ maxHeight: '75vh' }}
          />
        </Box>
      </Dialog>
    </>
  );
};
